import { Address, CRMNote } from "@/types";
import { SectionAvailability, CreatesEventType } from "@/pages/activities/activity-types/types";
import type { University } from "@/pages/education/universities/types";

export interface RegistrantRow {
  id: string;
  pgaId: string;
  profilePhoto: string;
  programType: string;
  registeredAt: string;
  canceledAt?: string | null;
  registrantName: string;
  sessionCount: number;
  orderStatus: OrderStatus;
  orderTotalPaid?: number;
  amountRefunded?: string;
  paymentStatus?: string | null;
  additionalGuests: number;
  notes?: CRMNote[];
  university?: { id: string; name: string } | null;
}

export enum OrderStatus {
  OPEN = "open",
  PENDING = "pending",
  PAID = "paid",
  FAILED = "failed",
  CANCELED = "canceled"
}

export enum EventRegistrationType {
  ALL = "ALL",
  INDIVIDUAL = "INDIVIDUAL"
}

export interface Person {
  id: string;
  pgaId: string;
  profilePhoto?: string;
  displayName: string;
  programType?: string;
}

export interface Registration {
  id: string;
  acceptedTerms: boolean;
  registeredAt: string;
  event: any;
  person: Person;
  customFields: {
    gender: string;
    dateOfBirth: string;
  };
}
export interface SessionRegistrations {
  id: string;
  attended: boolean | null;
  registration: Registration;
}

export interface EventRegistrant {
  id: string;
  pgaId: string;
  profilePhoto: string;
  programType: string;
  registeredAt: string;
  canceledAt?: string | null;
  registrantName: string;
  sessionCount: number;
  sessionRegistrations?: {
    id: string;
    attended: boolean | null;
  }[];
  order?: {
    status: OrderStatus;
    totalPaid?: number;
    totalRefunded?: number;
    paymentStatus?: string | null;
  };
  customFields: {
    additional_guests: number;
  };
  fulfillableRequirementId: string | null;
  notes?: CRMNote[];
  university?: { id: string; name: string } | null;
}

export interface EventRegistrantNode {
  node: EventRegistrant;
}

export interface EventRegistrants {
  eventRegistrants: {
    edges: EventRegistrantNode[];
    totalCount: number;
    pageInfo: {
      hasNextPage: boolean;
      hasPreviousPage: boolean;
      startCursor?: string;
      endCursor?: string;
    };
  };
}

export interface Variables {
  eventSlug?: string;
  search?: string;
  first?: number;
  last?: number;
  after?: string;
  before?: string;
  status?: RegistrationStatus;
}

export interface EventWrapper {
  event: Event;
}
export interface Event {
  id: string;
  status: string;
  title: string;
  description: string;
  createdBy?: {
    id: string;
    name: string;
  };
  hasWaitlist: boolean;
  waitlistEnabled: boolean;
  startsAt: string;
  endsAt: string;
  registrationOpenAt: string;
  registrationCloseAt: string;
  hasLateCancellation: boolean;
  cancelledAt: string;
  finalizedAt?: string | null;
  eventCode?: string;
  overrideRoles?: string[];
  eventType: {
    id: string;
    name: string;
  };
  activityType: {
    id: string;
    title: string;
    sectionAvailability: SectionAvailability;
    createsEvent: CreatesEventType;
    config?: {
      creditRates: {
        id: string;
        rateUnit: string;
        rateMultiplier: number;
        participationRole: string;
      }[];
    };
  };
  facility?: {
    id: string;
    name: string;
  };
  universities?: University[];
  collectsAttendance: boolean;
  sessionCount: number;
  registrationCount: number;
  registrationType: EventRegistrationType;
  hasActiveOrders: boolean;
  timezone: string;
  bannerImageUrl?: string;
  updatedAt: string;
  eventFees?: {
    id: string;
    paymentCollection: string;
    displayName?: string | null;
    amountCents?: number | null;
    productVariant?: {
      id: string;
      name: string;
      priceCents?: string | null;
    } | null;
  }[];
  sessions: {
    id: string;
    orderKey: number;
    startsAt: string;
    endsAt: string;
    maxAttendance?: number | null;
    registrations: SessionRegistrations[];
  }[];
  patEventAttributes: {
    finalized: boolean;
    hostProfessional: {
      id: string;
      pgaId: string;
      displayName: string;
      contactAddress: Address;
      pdrRequirements?: {
        requirements: {
          label: string;
        }[];
      };
    };
    examiner: {
      id: string;
      pgaId: string;
      displayName: string;
      contactAddress: Address;
      pdrRequirements?: {
        requirements: {
          label: string;
        }[];
      };
    };
    courseConditions: string;
    problemsIssues: string;
    yardageMale: string;
    courseRatingMale: string;
    yardageMaleFiftyPlus: string;
    yardageFemale: string;
    courseRatingFemale: string;
    yardageFemaleFiftyPlus: string;
    targetScoreMale: string;
    targetScoreFemale: string;
    collectionType: string;
    registrationFeeCents: number;
    courseFeeCents: number;
    examinerFeePayee: string;
    submittedForPaymentAt: string;
  };
  recruitmentLeadFormUrl?: string;
  recruitmentEventAttributes?: {
    id?: string;
    recruitmentEventType?: string;
    purpose?: string;
    strategicFunding?: string;
    pgaRepresentation?: string[];
    supportRequested?: string[];
    estimatedExpenses?: number;
    sponsorshipAsk?: number;
    estimatedParents?: number;
    estimatedCoaches?: number;
    estimatedOtherAttendees?: number;
    totalAttendees?: number;
    staffAttendees?: {
      id: string;
      displayName: string;
      section?: {
        id: string;
        name: string;
      };
      email?: string;
      phone?: string;
    }[];
    additionalComments?: string;
    eventRecap?: string;
    overallFeedback?: string;
    eventRating?: number;
    commentsAndQuotes?: string;
    photoUploads?: string[];
    isFinalized?: boolean;
    createdAt?: string;
    updatedAt?: string;
  };
  customFields: string[];
  section: {
    id: string;
    name: string;
    permissions: {
      manageEvents: boolean;
    };
  };
  totalFeesCollected: number;
  registrationFeesCollected: number;
  courseFeesCollected: number;
  locationType: string;
  location: string;
  address1: string;
  city: string;
  state: string;
  zip: string;
  photoUploads: string[];
  university?: {
    id: string;
    legacyId: string;
    name: string;
  } | null;
}

export enum RegistrationStatus {
  ALL = "ALL",
  CANCELED = "CANCELED",
  REGISTERED = "REGISTERED"
}

export interface TransferEventSearchNode {
  id: string;
  title: string;
  slug: string;
  startsAt: string;
  endsAt: string;
  city?: string;
  state?: string;
  eventCode?: string | null;
  facility?: { id: string; name: string };
}

export interface TransferEligibleEventsResult {
  eligibleEvents: {
    edges: { node: TransferEventSearchNode }[];
    totalCount: number;
  };
}

//  TODO: Remove this once the activityType query is updated
export enum ActivityParticipationRole {
  HOST_PROFESSIONAL = "HOST_PROFESSIONAL",
  EXAMINER = "EXAMINER"
}
